import React, { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { QuipliLink } from '../../../components/link/quipli-link';
import { CashBasisTaxReportRow, TaxReportRequest } from '../../../clients/quipli-service/report/report';
import { useReportApiV2 } from '../../../clients/quipli-service/report/report-api-v2';
import { Spinner } from '../../../components/spinner/spinner';
import { Typography } from '../../../components/typography/typography';
import { Card } from '../../../components/card/card';
import { displayMoneyFormat } from '../../../util/util';
import { PaymentMethodFormat } from '../../collectibles/invoice-list';
import './tax-report.scss';
import _ from 'lodash';

export const CashBasisTaxReport = ({
  reportRequest,
  currency,
}: {
  reportRequest: TaxReportRequest;
  currency?: string;
}) => {
  const { cashBasisTaxReport } = useReportApiV2();
  const [report, setReport] = useState<CashBasisTaxReportRow[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const loadReport = async () => {
      setIsLoading(true);
      const report = await cashBasisTaxReport(reportRequest);
      setReport(report);
      setIsLoading(false);
    };

    if (reportRequest.from && reportRequest.to) {
      loadReport();
    }
  }, [reportRequest]);

  return (
    <>
      <div className="flex flex-wrap gap-1">
        <Card className="w-[calc(20%-0.25rem)] p-4 flex flex-column gap-2 text-[0.9rem]">
          <div className="text-[var(--grey-500)]">Total Payments</div>
          <div className="font-medium text-[var(--quipli-black-800)]">
            {displayMoneyFormat(_.sum(report?.map((r) => r.Amount)), currency)}
          </div>
        </Card>
        <Card className="w-[calc(20%-0.25rem)] p-4 flex flex-column gap-2 text-[0.9rem]">
          <div className="text-[var(--grey-500)]">Total Tax</div>
          <div className="font-medium text-[var(--quipli-black-800)]">
            {displayMoneyFormat(_.sum(report?.map((r) => r.Tax)), currency)}
          </div>
        </Card>
      </div>
      <Card>
        <table className="w-full">
          <thead>
            <tr>
              <th className="text-left">Customer Name</th>
              <th className="text-left">Invoice Number</th>
              <th className="text-left">Payment Method</th>
              <th className="text-left">Date</th>
              <th className="text-left">Location</th>
              <th className="text-right">Amount</th>
              <th className="text-right">Tax</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={100}>
                  <Spinner />
                </td>
              </tr>
            ) : (
              <>
                {currency && report && (
                  <>
                    {report.length > 0 ? (
                      report.map((report, i) => <TableRow key={i} report={report} currency={currency} />)
                    ) : (
                      <tr>
                        <td colSpan={7}>No record found</td>
                      </tr>
                    )}
                  </>
                )}
              </>
            )}
          </tbody>
        </table>
      </Card>
    </>
  );
};

const TableRow = ({ key, report, currency }: { key: number; report: CashBasisTaxReportRow; currency: string }) => {
  return (
    <tr key={key}>
      <td>
        <QuipliLink
          className="underline underline-offset-1 text-[var(--quipli-black-800)]"
          to={`users/customer_form/${report['Customer ID']}`}
        >
          {report['Customer Name']}
        </QuipliLink>
      </td>
      <td>
        <QuipliLink
          className="underline underline-offset-1 text-[var(--quipli-black-800)]"
          to={`orders/view/${report['Order ID']}`}
        >
          {report['Invoice Number']}
        </QuipliLink>
      </td>
      <td>{PaymentMethodFormat(report['Method'], report['Method Detail'])}</td>
      <td className="Date">
        <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
          {DateTime.fromISO(report['Date']).toFormat('LLL-dd')}
        </Typography>
        <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
          {report['Time']}
        </Typography>
      </td>
      <td>{report['Location']}</td>
      <td className="text-right">{displayMoneyFormat(report['Amount'], currency)}</td>
      <td className="text-right">{displayMoneyFormat(report['Tax'], currency)}</td>
    </tr>
  );
};
